import { supabase } from '../lib/supabase';

/**
 * Service for fetching performance and platform usage metrics
 * Focused on real, actionable data for billing and platform management
 */

/**
 * Get platform-wide usage statistics
 * Single efficient RPC call that returns comprehensive platform metrics
 * @returns {Promise<Object>} Platform statistics
 */
export const getPlatformStatistics = async () => {
  try {
    const { data, error } = await supabase.rpc('get_platform_statistics');

    if (error) {
      console.error('get_platform_statistics RPC error:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.warn('No platform statistics returned, data:', data);
      return null;
    }

    // RPC returns single row with all stats
    const stats = data[0];

    return {
      users: {
        total: stats.total_users || 0,
        contractors: stats.total_contractors || 0,
        corporate: stats.total_corporate_users || 0,
        superAdmins: stats.total_super_admins || 0,
        active30d: stats.active_users_30d || 0
      },
      companies: {
        total: stats.total_companies || 0,
        active: stats.active_companies || 0,
        suspended: stats.suspended_companies || 0
      },
      jobs: {
        total: stats.total_jobs || 0,
        instances: stats.total_job_instances || 0,
        completed: stats.completed_jobs || 0
      },
      storage: {
        totalPhotos: stats.total_photos || 0,
        totalMB: stats.total_storage_mb || 0,
        avgPhotoSizeMB: stats.avg_photo_size_mb || 0
      },
      database: {
        totalRecords: stats.total_records || 0,
        auditLogs: stats.total_audit_logs || 0
      }
    };
  } catch (error) {
    console.error('Error fetching platform statistics:', error);
    return null;
  }
};

/**
 * Get API latency metrics by measuring actual Supabase query performance
 * @returns {Promise<Object>} Latency metrics (avg, p50, p95, p99)
 */
export const getApiLatencyMetrics = async () => {
  try {
    const measurements = [];

    // Run 10 sample queries to measure latency
    for (let i = 0; i < 10; i++) {
      const startTime = performance.now();
      await supabase.from('companies').select('id').limit(1).single();
      const endTime = performance.now();
      measurements.push(endTime - startTime);
    }

    // Sort measurements for percentile calculations
    measurements.sort((a, b) => a - b);

    // Calculate metrics
    const avg = Math.round(measurements.reduce((a, b) => a + b, 0) / measurements.length);
    const p50 = Math.round(measurements[Math.floor(measurements.length * 0.5)]);
    const p95 = Math.round(measurements[Math.floor(measurements.length * 0.95)]);
    const p99 = Math.round(measurements[Math.floor(measurements.length * 0.99)]);

    return { avg, p50, p95, p99 };
  } catch (error) {
    console.error('Error measuring API latency:', error);
    return { avg: 0, p50: 0, p95: 0, p99: 0 };
  }
};

/**
 * Get largest companies by data volume using efficient RPC function
 * @param {number} limit - Number of companies to return
 * @returns {Promise<Array>} Largest companies
 */
export const getLargestCompanies = async (limit = 5) => {
  try {
    // Use RPC function for efficient query
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_company_data_volume', {
      p_limit: limit
    });

    if (!rpcError && rpcData && rpcData.length > 0) {
      console.log(`Found ${rpcData.length} companies via RPC`);

      return rpcData.map(company => ({
        id: company.company_id,
        name: company.company_name,
        tag: company.company_tag,
        jobCount: company.job_count,
        userCount: company.user_count,
        photoCount: company.photo_count,
        storageMB: company.total_storage_mb,
        dataVolume: `${company.job_count + company.user_count + company.photo_count} records`
      }));
    }

    // Fallback to simple query if RPC not available
    console.warn('RPC function not available, using fallback');

    const { data: companies, error } = await supabase
      .from('companies')
      .select('id, company_name, company_tag')
      .limit(limit);

    if (error) throw error;

    if (!companies || companies.length === 0) {
      return [];
    }

    // Get job and user counts for each company
    const companiesWithData = await Promise.all(
      companies.map(async (company) => {
        const [jobsResult, usersResult] = await Promise.all([
          supabase.from('jobs').select('id', { count: 'exact', head: true }).eq('company_id', company.id),
          supabase.from('company_users').select('id', { count: 'exact', head: true }).eq('company_id', company.id)
        ]);

        const jobCount = jobsResult.count || 0;
        const userCount = usersResult.count || 0;

        return {
          id: company.id,
          name: company.company_name,
          tag: company.company_tag,
          jobCount,
          userCount,
          photoCount: 0,
          storageMB: 0,
          dataVolume: `${jobCount + userCount} records`
        };
      })
    );

    // Sort by data volume and return top N
    return companiesWithData
      .sort((a, b) => (b.jobCount + b.userCount) - (a.jobCount + a.userCount))
      .slice(0, limit);
  } catch (error) {
    console.error('Error fetching largest companies:', error);
    return [];
  }
};

/**
 * Get contractors with most uploads/storage usage
 * Uses RPC function for efficient database query instead of individual API calls
 * @param {number} limit - Number of contractors to return
 * @returns {Promise<Array>} Heaviest contractors by uploads
 */
export const getHeaviestContractors = async (limit = 5) => {
  try {
    // Use RPC function to get contractor storage usage efficiently
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_contractor_storage_usage', {
      p_limit: limit
    });

    if (!rpcError && rpcData && rpcData.length > 0) {
      console.log(`Found ${rpcData.length} contractors with storage data via RPC`);

      return rpcData.map(contractor => ({
        id: contractor.contractor_id,
        name: contractor.contractor_name,
        email: contractor.contractor_email,
        uploads: contractor.total_uploads,
        storageUsed: `${contractor.total_size_mb} MB`,
        avgFileSize: `${contractor.avg_size_mb} MB`
      }));
    }

    // Fallback: RPC failed or not available, use simple upload count
    console.warn('RPC function not available, using fallback method:', rpcError);

    const { data: photos, error: photosError } = await supabase
      .from('job_photos')
      .select(`
        uploaded_by,
        user_profiles!job_photos_uploaded_by_fkey (
          id,
          email,
          full_name
        )
      `);

    if (photosError) throw photosError;

    if (!photos || photos.length === 0) {
      console.log('No photo uploads found');
      return [];
    }

    // Group photos by contractor and count uploads
    const contractorStats = {};

    photos.forEach(photo => {
      const contractorId = photo.uploaded_by;
      if (!contractorStats[contractorId]) {
        const user = photo.user_profiles;
        contractorStats[contractorId] = {
          id: contractorId,
          name: user?.full_name || 'Unknown',
          email: user?.email || 'N/A',
          uploads: 0
        };
      }
      contractorStats[contractorId].uploads++;
    });

    // Return sorted by upload count (no file sizes in fallback)
    return Object.values(contractorStats)
      .map(contractor => ({
        ...contractor,
        storageUsed: 'N/A',
        avgFileSize: 'N/A'
      }))
      .sort((a, b) => b.uploads - a.uploads)
      .slice(0, limit);

  } catch (error) {
    console.error('Error fetching heaviest contractors:', error);
    return [];
  }
};

/**
 * Get all performance metrics at once
 * Only includes useful, real metrics
 * @returns {Promise<Object>} All performance metrics
 */
export const getAllPerformanceMetrics = async () => {
  try {
    const [
      platformStats,
      apiLatency,
      largestCompanies,
      heaviestContractors
    ] = await Promise.all([
      getPlatformStatistics(),
      getApiLatencyMetrics(),
      getLargestCompanies(),
      getHeaviestContractors()
    ]);

    return {
      platformStats,
      apiLatency,
      largestCompanies,
      heaviestContractors
    };
  } catch (error) {
    console.error('Error fetching all performance metrics:', error);
    throw error;
  }
};

export default {
  getPlatformStatistics,
  getApiLatencyMetrics,
  getLargestCompanies,
  getHeaviestContractors,
  getAllPerformanceMetrics
};
